import React, { useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  FormControlLabel,
  Checkbox,
  Typography,
  Divider,
  Grid,
  Paper,
  Avatar,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { useSelector } from "react-redux";
import { selectCurrentagency } from "../../redux/features/authSlice";
import { Controller, FormProvider, useForm } from "react-hook-form";
import AutoComplete from "../../components/FormElements/AutoComplete";
import { PersonOutline, CheckCircleOutline } from "@mui/icons-material";
import { LEAD_PERMISSIONS } from "./permissions";
import { toast } from "react-toastify";
import { useGetRolesQuery, usePostLeadsPermissionsMutation } from "../../redux/api/rolesApi";

export default function LeadPermissions({
  open,
  onClose,
  selectedLead,
  refetch,
}) {
  const currentAgency = useSelector(selectCurrentagency);

  const methods = useForm({
    defaultValues: {
      role: "",
      // Lead Information
      firstname: false,
      lastname: false,
      history: false,
      propertydata: false,
      description: false,
      leadtype: false,
      leadTime: false,
      lastUpdatedDate: false,
      leadTag: false,
      leadStatus: false,
      // Source Information
      source: false,
      medium: false,
      priority: false,
      // Professional Information
      companyName: false,
      designation: false,
      budget: false,
      location: false,
      // Property Information
      leadSerialNo: false,
      propertyRefNo: false,
      societyName: false,
      fileNo: false,
      block: false,
      plotNo: false,
      plotSize: false,
      propertyType: false,
      additionalNo: false,
      // Contact Information
      email: false,
      membershipId: false,
      type: false,
      whatsappNo: false,
      moreinfo: false,
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = methods;
  const [updateLeadPermissions] = usePostLeadsPermissionsMutation();
  const { data: roles, isFetching: isFetchingRoles } = useGetRolesQuery();

  useEffect(() => {
    if (selectedLead) {
      reset({
        role: selectedLead.roleId, // Include roleId for updates
        agencyId: currentAgency.agencyid,
        // Lead Information
        firstname: Boolean(selectedLead.firstname),
        lastname: Boolean(selectedLead.lastname),
        history: Boolean(selectedLead.history),
        propertydata: Boolean(selectedLead.propertydata),
        description: Boolean(selectedLead.description),
        leadtype: Boolean(selectedLead.leadtype),
        leadTime: Boolean(selectedLead.leadTime),
        lastUpdatedDate: Boolean(selectedLead.lastUpdatedDate),
        leadTag: Boolean(selectedLead.leadTag),
        leadStatus: Boolean(selectedLead.leadStatus),
        // Source Information
        source: Boolean(selectedLead.source),
        medium: Boolean(selectedLead.medium),
        priority: Boolean(selectedLead.priority),
        // Professional Information
        companyName: Boolean(selectedLead.companyName),
        designation: Boolean(selectedLead.designation),
        budget: Boolean(selectedLead.budget),
        location: Boolean(selectedLead.location),
        // Property Information
        leadSerialNo: Boolean(selectedLead.leadSerialNo),
        propertyRefNo: Boolean(selectedLead.propertyRefNo),
        societyName: Boolean(selectedLead.societyName),
        fileNo: Boolean(selectedLead.fileNo),
        block: Boolean(selectedLead.block),
        plotNo: Boolean(selectedLead.plotNo),
        plotSize: Boolean(selectedLead.plotSize),
        propertyType: Boolean(selectedLead.propertyType),
        additionalNo: Boolean(selectedLead.additionalNo),
        // Contact Information
        email: Boolean(selectedLead.email),
        membershipId: Boolean(selectedLead.membershipId),
        type: Boolean(selectedLead.type),
        whatsappNo: Boolean(selectedLead.whatsappNo),
        moreinfo: Boolean(selectedLead.moreinfo),
      });
    } else {
      reset({
        role: "", // Reset roleId for new entries
        agencyId: currentAgency.agencyid,
        // Lead Information
        firstname: false,
        lastname: false,
        history: false,
        propertydata: false,
        description: false,
        leadtype: false,
        leadTime: false,
        lastUpdatedDate: false,
        leadTag: false,
        leadStatus: false,
        // Source Information
        source: false,
        medium: false,
        priority: false,
        // Professional Information
        companyName: false,
        designation: false,
        budget: false,
        location: false,
        // Property Information
        leadSerialNo: false,
        propertyRefNo: false,
        societyName: false,
        fileNo: false,
        block: false,
        plotNo: false,
        plotSize: false,
        propertyType: false,
        additionalNo: false,
        // Contact Information
        email: false,
        membershipId: false,
        type: false,
        whatsappNo: false,
        moreinfo: false,
      });
    }
  }, [selectedLead, reset, currentAgency.agencyid]);

  const onSubmit = async (data) => {

    const formattedData = {
      role: selectedLead ? selectedLead.role : data.roleId, // Use existing roleId for updates
      agencyId: currentAgency.agencyid,
      // Lead Information
      firstname: data.firstname ? 1 : 0,
      lastname: data.lastname ? 1 : 0,
      history: data.history ? 1 : 0,
      propertydata: data.propertydata ? 1 : 0,
      description: data.description ? 1 : 0,
      leadtype: data.leadtype ? 1 : 0,
      leadTime: data.leadTime ? 1 : 0,
      lastUpdatedDate: data.lastUpdatedDate ? 1 : 0,
      leadTag: data.leadTag ? 1 : 0,
      leadStatus: data.leadStatus ? 1 : 0,
      // Source Information
      source: data.source ? 1 : 0,
      medium: data.medium ? 1 : 0,
      priority: data.priority ? 1 : 0,
      // Professional Information
      companyName: data.companyName ? 1 : 0,
      designation: data.designation ? 1 : 0,
      budget: data.budget ? 1 : 0,
      location: data.location ? 1 : 0,
      // Property Information
      leadSerialNo: data.leadSerialNo ? 1 : 0,
      propertyRefNo: data.propertyRefNo ? 1 : 0,
      societyName: data.societyName ? 1 : 0,
      fileNo: data.fileNo ? 1 : 0,
      block: data.block ? 1 : 0,
      plotNo: data.plotNo ? 1 : 0,
      plotSize: data.plotSize ? 1 : 0,
      propertyType: data.propertyType ? 1 : 0,
      additionalNo: data.additionalNo ? 1 : 0,
      // Contact Information
      email: data.email ? 1 : 0,
      membershipId: data.membershipId ? 1 : 0,
      type: data.type ? 1 : 0,
      whatsappNo: data.whatsappNo ? 1 : 0,
      moreinfo: data.moreinfo ? 1 : 0,
    };
    
    try {
      await updateLeadPermissions(formattedData).unwrap();
      refetch();
      handleClose();
    } catch (error) {
      console.error("Error updating lead permissions:", error);
      const errorMsg = error?.data?.message || error?.data?.Message;
      toast.error(
        errorMsg || "Something went wrong while updating lead permissions"
      );
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <Avatar sx={{ bgcolor: "primary.main", mr: 2 }}>
            <PersonOutline />
          </Avatar>
          <Typography variant="h3" component="div">
            {selectedLead ? "Edit Lead Permissions" : "Assign Lead Permissions"}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" mt={1}>
          Configure which lead fields agents can view.
        </Typography>
      </DialogTitle>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent dividers>
            {!selectedLead && (
              <Box mb={4}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Select Role
                </Typography>
                <Controller
                  name="roleId"
                  control={control}
                  rules={{ required: !selectedLead && "Role is required" }}
                  render={({ field, fieldState: { error } }) => (
                    <AutoComplete
                      {...field}
                      isLoading={isFetchingRoles}
                      options={
                        roles?.map((role) => ({
                          roleId: role.roleId,
                          roleName: role.roleName,
                        })) || []
                      }
                      label="Choose a role"
                      optionId="roleId"
                      optionLabel="roleName"
                      error={!!error}
                      helperText={error?.message}
                      onChange={(e, newValue) => {
                        field.onChange(newValue?.roleId || "");
                      }}
                      value={
                        roles?.find((role) => role.roleId === field.value) || null
                      }
                    />
                  )}
                />
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            <Box mt={3}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Lead Field Access Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Enable or disable access to specific lead fields for this Role
              </Typography>

              <Grid container spacing={3}>
                {LEAD_PERMISSIONS.map((group) => (
                  <Grid item xs={12} md={6} key={group.category}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                      }}
                    >
                      <Box display="flex" alignItems="center" mb={2}>
                        {group.icon}
                        <Typography variant="subtitle2" fontWeight={600} ml={1}>
                          {group.category}
                        </Typography>
                      </Box>

                      <Box sx={{ pl: 1 }}>
                        {group.fields.map((field) => (
                          <Controller
                            key={field.name}
                            name={field.name}
                            control={control}
                            render={({ field: controllerField }) => (
                              <Tooltip
                                title={field.description}
                                arrow
                                placement="right"
                              >
                                <Paper
                                  elevation={0}
                                  sx={{
                                    p: 1,
                                    mb: 1,
                                    borderRadius: 1,
                                    backgroundColor: controllerField.value
                                      ? "#e8f5e9"
                                      : "transparent",
                                    transition: "background-color 0.3s ease",
                                  }}
                                >
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        {...controllerField}
                                        checked={controllerField.value}
                                        icon={
                                          <CheckCircleOutline color="disabled" />
                                        }
                                        checkedIcon={
                                          <CheckCircleOutline color="success" />
                                        }
                                      />
                                    }
                                    label={
                                      <Box>
                                        <Typography variant="body2">
                                          {field.label}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          {field.description}
                                        </Typography>
                                      </Box>
                                    }
                                    sx={{ width: "100%" }}
                                  />
                                </Paper>
                              </Tooltip>
                            )}
                          />
                        ))}
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              p: 3,
              borderTop: "1px solid #e0e0e0",
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{ minWidth: 120 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              sx={{ minWidth: 120, color: "white" }}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : null
              }
            >
              {isSubmitting ? "Saving..." : "Save"}
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
}